import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface SettingChange {
  id: string;
  device_tag: string;
  device_id: string;
  status: string;
  priority: string;
  recommended_date: string | null;
  acknowledged_date: string | null;
  implemented_date: string | null;
  verified_date: string | null;
  notes: string | null;
}

const statusOrder = ['recommended', 'acknowledged', 'scheduled', 'implemented', 'verified'];

const statusColors: Record<string, { bg: string; color: string; border: string; dot: string }> = {
  recommended: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1565c0' },
  acknowledged: { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8', dot: '#7b1fa2' },
  scheduled: { bg: '#fff3e0', color: '#e65100', border: '#ffcc80', dot: '#e65100' },
  implemented: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', dot: '#2e7d32' },
  verified: { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4', dot: '#00695c' },
};

const statusStyle = (status: string): React.CSSProperties => {
  const s = statusColors[status?.toLowerCase()];
  if (!s) return { background: '#f5f5f5', color: '#757575', border: '1px solid #e0e0e0' };
  return { background: s.bg, color: s.color, border: `1px solid ${s.border}` };
};

const priorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return '#f44336';
    case 'high': return '#ff5722';
    case 'medium': return '#ff9800';
    case 'low': return '#8bc34a';
    default: return '#888';
  }
};

const statusLabel = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Info Panel ──────────────────────────────────────────

const stageDescriptions = [
  { status: 'recommended', text: 'JMB issues a protection study report recommending new or revised settings for a device' },
  { status: 'acknowledged', text: 'The site EEM (Electrical Engineering Manager) confirms they have reviewed and accepted the recommendation' },
  { status: 'scheduled', text: 'The site has planned a date for the electrician to implement the setting change (optional — can skip straight to Implemented)' },
  { status: 'implemented', text: 'The electrician has physically changed the protection relay settings on site' },
  { status: 'verified', text: 'An injection test has been performed confirming the new settings are operating correctly' },
];

const InfoPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: '#fff',
      borderRadius: 10,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      marginBottom: 24,
      overflow: 'hidden',
      border: '1.5px solid #e8eaf0',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: open ? '#f0f4ff' : '#fff',
          border: 'none',
          padding: '14px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 14,
          fontWeight: 600,
          color: '#1a1a2e',
          fontFamily: 'inherit',
          transition: 'background 0.2s',
        }}
      >
        <span>ℹ️ How Setting Changes Work</span>
        <span style={{ fontSize: 12, color: '#888', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
      </button>
      {open && (
        <div style={{ padding: '4px 20px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            {stageDescriptions.map((stage, idx) => {
              const col = statusColors[stage.status];
              return (
                <div key={stage.status} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    minWidth: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: col?.bg || '#f5f5f5',
                    border: `2px solid ${col?.dot || '#ccc'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    color: col?.color || '#555',
                    flexShrink: 0,
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                    <strong style={{ color: col?.color || '#333' }}>{statusLabel(stage.status)}</strong>
                    <span style={{ color: '#555' }}> — {stage.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{
            marginTop: 16,
            fontSize: 12,
            color: '#666',
            background: '#fafbfc',
            borderRadius: 6,
            padding: '10px 14px',
            borderLeft: '3px solid #ff9800',
          }}>
            Changes can be reverted to any previous stage if an error is made. A reason must be provided for any revert.
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────

const PendingChanges: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [changes, setChanges] = useState<SettingChange[]>([]);
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchChanges = useCallback(async () => {
    if (!siteId) return;
    try {
      const [siteRes, changesRes] = await Promise.all([
        api.get(`/api/sites/${siteId}`),
        api.get(`/api/sites/${siteId}/setting-changes`),
      ]);
      setSiteName(siteRes.data.name);
      setChanges(changesRes.data || []);
    } catch {
      setError('Failed to load pending changes.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const pendingOnly = changes.filter((c) =>
    ['recommended', 'acknowledged', 'scheduled'].includes(c.status?.toLowerCase())
  );

  const filteredChanges = filterStatus
    ? changes.filter((c) => c.status?.toLowerCase() === filterStatus)
    : changes;

  const pendingCount = pendingOnly.length;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(`/site/${siteId}`)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Site Overview
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{siteName || 'Pending Changes'}</div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>⚠️ SETTING CHANGES</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32 }}>
        {loading && <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>Loading changes...</div>}
        {error && (
          <div style={{
            background: '#fff5f5', border: '1px solid #f44336', borderRadius: 8,
            color: '#f44336', padding: '14px 18px', marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && (
          <>
            {/* Info panel */}
            <InfoPanel />

            {/* Summary + status filter */}
            <div style={{
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              padding: '16px 20px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}>
              {pendingCount > 0 && (
                <div style={{
                  background: '#fff8e1',
                  border: '1px solid #ff9800',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 13,
                  color: '#7a4e00',
                  fontWeight: 600,
                }}>
                  ⚠️ {pendingCount} change{pendingCount !== 1 ? 's' : ''} pending action
                </div>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => setFilterStatus('')}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 6,
                    border: filterStatus === '' ? '2px solid #0f3460' : '1.5px solid #e0e0e0',
                    background: filterStatus === '' ? '#0f3460' : '#fff',
                    color: filterStatus === '' ? '#fff' : '#555',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  All ({changes.length})
                </button>
                {statusOrder.map((s) => {
                  const count = changes.filter((c) => c.status?.toLowerCase() === s).length;
                  return (
                    <button
                      key={s}
                      onClick={() => setFilterStatus(s)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 6,
                        border: filterStatus === s ? '2px solid #0f3460' : '1.5px solid #e0e0e0',
                        background: filterStatus === s ? '#0f3460' : '#fff',
                        color: filterStatus === s ? '#fff' : '#555',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        textTransform: 'capitalize',
                      }}
                    >
                      {s} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {filteredChanges.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 14 }}>No changes found.</div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 18,
              }}>
                {filteredChanges.map((c) => (
                  <ChangeCard key={c.id} change={c} siteId={siteId!} onRefresh={fetchChanges} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

// ─── ChangeCard ──────────────────────────────────────────

const ChangeCard: React.FC<{ change: SettingChange; siteId: string; onRefresh: () => void }> = ({ change: c, siteId, onRefresh }) => {
  const statusIdx = statusOrder.indexOf(c.status?.toLowerCase());
  const nextStatus = statusIdx < statusOrder.length - 1 ? statusOrder[statusIdx + 1] : null;
  const previousStatuses = statusOrder.slice(0, statusIdx);

  const [advancing, setAdvancing] = useState(false);
  const [showRevert, setShowRevert] = useState(false);
  const [revertTarget, setRevertTarget] = useState(previousStatuses[0] || '');
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);
  const [actionError, setActionError] = useState('');

  const handleAdvance = async () => {
    if (!nextStatus) return;
    setAdvancing(true);
    setActionError('');
    try {
      await api.patch(`/api/setting-changes/${c.id}/status`, { status: nextStatus });
      onRefresh();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to advance status.');
    } finally {
      setAdvancing(false);
    }
  };

  const handleRevert = async () => {
    if (!revertTarget || !revertReason.trim()) return;
    setReverting(true);
    setActionError('');
    try {
      await api.patch(`/api/setting-changes/${c.id}/status`, { status: revertTarget, notes: revertReason.trim() });
      setShowRevert(false);
      setRevertReason('');
      onRefresh();
    } catch (err: any) {
      setActionError(err?.response?.data?.detail || 'Failed to revert status.');
    } finally {
      setReverting(false);
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
      overflow: 'hidden',
      border: '1.5px solid #e8eaf0',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Card header */}
      <div style={{
        background: '#f8f9fc',
        borderBottom: '1px solid #eee',
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>{c.device_tag}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Device ID: {c.device_id?.slice(0, 8)}…</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{
            background: priorityColor(c.priority) + '22',
            color: priorityColor(c.priority),
            border: `1px solid ${priorityColor(c.priority)}44`,
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'capitalize',
          }}>
            {c.priority}
          </span>
          <span style={{
            ...statusStyle(c.status),
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'capitalize',
          }}>
            {c.status}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '10px 18px 4px' }}>
        <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>
          {statusOrder.map((s, idx) => (
            <div
              key={s}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: idx <= statusIdx ? '#0f3460' : '#e0e0e0',
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {statusOrder.map((s) => <span key={s}>{s.slice(0, 3)}</span>)}
        </div>
      </div>

      {/* Dates */}
      <div style={{ padding: '10px 18px 14px', flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {c.recommended_date && <DateRow label="Recommended" date={c.recommended_date} />}
          {c.acknowledged_date && <DateRow label="Acknowledged" date={c.acknowledged_date} />}
          {c.implemented_date && <DateRow label="Implemented" date={c.implemented_date} />}
          {c.verified_date && <DateRow label="Verified" date={c.verified_date} />}
        </div>
        {c.notes && (
          <div style={{
            marginTop: 10,
            fontSize: 12,
            color: '#555',
            background: '#f8f9fc',
            borderRadius: 6,
            padding: '8px 12px',
            lineHeight: 1.5,
            maxHeight: 80,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}>
            {c.notes}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{
        borderTop: '1px solid #eee',
        padding: '12px 18px',
        background: '#fafbfc',
      }}>
        {actionError && (
          <div style={{ fontSize: 12, color: '#f44336', marginBottom: 8 }}>{actionError}</div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {nextStatus && (
            <button
              onClick={handleAdvance}
              disabled={advancing}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 6,
                border: 'none',
                background: advancing ? '#a5d6a7' : '#43a047',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: advancing ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: advancing ? 0.7 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {advancing ? 'Advancing…' : `→ ${statusLabel(nextStatus)}`}
            </button>
          )}
          {previousStatuses.length > 0 && (
            <button
              onClick={() => { setShowRevert(!showRevert); setActionError(''); }}
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #ef6c00',
                background: showRevert ? '#fff3e0' : '#fff',
                color: '#ef6c00',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              ↩ Revert
            </button>
          )}
        </div>

        {showRevert && previousStatuses.length > 0 && (
          <div style={{
            marginTop: 10,
            background: '#fff8f0',
            border: '1px solid #ffcc80',
            borderRadius: 8,
            padding: 12,
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Revert to:</div>
            <select
              value={revertTarget}
              onChange={(e) => setRevertTarget(e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px',
                borderRadius: 6,
                border: '1px solid #ddd',
                fontSize: 13,
                fontFamily: 'inherit',
                marginBottom: 8,
                textTransform: 'capitalize',
              }}
            >
              {previousStatuses.map((s) => (
                <option key={s} value={s}>{statusLabel(s)}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Reason for revert (required)"
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px',
                borderRadius: 6,
                border: `1px solid ${revertReason.trim() ? '#ddd' : '#f44336'}`,
                fontSize: 13,
                fontFamily: 'inherit',
                marginBottom: 8,
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleRevert}
              disabled={reverting || !revertReason.trim()}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: 'none',
                background: reverting || !revertReason.trim() ? '#ccc' : '#ef6c00',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: reverting || !revertReason.trim() ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {reverting ? 'Reverting…' : 'Confirm Revert'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const DateRow: React.FC<{ label: string; date: string }> = ({ label, date }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
    <span style={{ color: '#888' }}>{label}:</span>
    <span style={{ color: '#333', fontWeight: 500 }}>{date}</span>
  </div>
);

export default PendingChanges;
